import React, { useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { FormControl, MenuItem, Select } from '@material-ui/core';
import Switch from '@material-ui/core/Switch';
import T from 'components/common/text/T';
import useInput from 'lib/hooks/useInput';
import { useShallowAppSelector } from 'store/hooks';
import { UserActions, UtilActions } from 'store/actionCreators';
import { useDidUpdateEffect } from 'lib/utils';
import { ICurrency, ICountry } from 'lib/types/sharedTypes';
import { IUserProfile } from 'lib/types/userTypes';
import { icon_user_circle } from 'components/base/images';
import ImgCrop from 'components/common/images/ImgCrop';
import MuiWrapper from 'components/common/input/MuiWrapper';
import ProfileAuthConnectContainer from 'containers/user/ProfileAuthConnectContainer';
import cx from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import { useTranslation } from 'react-i18next';

function MasterProfileContainer() {
  const {
    user,
    fetchProfile: { profile: userProfile },
    fetchProfileSuccess,
    fetchCountryList: { countryList },
    fetchCurrencyList: { currencyList },
    updateUserCurrecnySuccess,
    updateUserVisibilitySuccess,
  }: {
    user: IUserProfile;
    fetchProfile: { profile: IUserProfile };
    fetchProfileSuccess: boolean;
    fetchCountryList: {
      countryList: ICountry[];
    };
    fetchCurrencyList: {
      currencyList: ICurrency[];
    };
    updateUserCurrecnySuccess: boolean;
    updateUserVisibilitySuccess: boolean;
  } = useShallowAppSelector(state => ({
    user: state.user.user || {},
    fetchProfile: state.user.fetchProfile.data || {},
    fetchProfileSuccess: state.user.fetchProfile.success,
    fetchCountryList: state.util.countryList.data || {},
    fetchCurrencyList: state.util.currencyList.data || {},

    updateUserCurrecnySuccess: state.user.updateUserCurrecny.success,
    updateUserVisibilitySuccess: state.user.updateUserVisibility.success,
  }));

  const { t } = useTranslation();
  const profileRef = useRef<null | any>(null);
  const currency = useInput('');
  const visibility = useInput(false);

  const [tooltipText, setTooltipText] = useState<string>(t('GLOBAL_PUBLIC_DETAIL'));

  useEffect(() => {
    UserActions.fetch_profile_request();
    UtilActions.fetch_country_list_request();
    UtilActions.fetch_currency_list_request();
  }, []);

  useDidUpdateEffect(() => {
    if (fetchProfileSuccess) {
      currency.setValue(userProfile.currencyCode);
      visibility.setValue(userProfile.visibility === 0 ? true : false);
    }
  }, [fetchProfileSuccess]);

  useDidUpdateEffect(() => {
    if (updateUserCurrecnySuccess) {
      UserActions.fetch_profile_request();
    }
  }, [updateUserCurrecnySuccess]);

  useDidUpdateEffect(() => {
    if (updateUserVisibilitySuccess) {
      visibility.setValue(!visibility.value);
    }
  }, [updateUserVisibilitySuccess]);

  const handleChangeCurrency = (e: any) => {
    currency.setValue(e.target.value);
    const submitData = {
      currency: e.target.value,
    };
    UserActions.update_user_currency_request(submitData);
  };

  const handleChangeVisibilty = () => {
    //visibility : 0 => 활성화, 1 => 비활성화
    UserActions.update_user_visibility_request({ visibility: !visibility.value ? 0 : 1 });
  };

  return (
    <MasterProfile data-component-name="MasterProfile">
      <div className="masterProfile__wrapper">
        <div className="masterProfile__container">
          <div className="masterProfile__thumnail_container">
            <div className="masterProfile__thumnail_row_box image">
              <label htmlFor="profileImage" className="masterProfile__image_box">
                {!!userProfile?.profileImg ? (
                  <ImgCrop width={130} isCircle src={userProfile.profileImg} alt="profile image" />
                ) : (
                  <img className="masterProfile__image" src={icon_user_circle} />
                )}
              </label>
            </div>
            <div className="masterProfile__thumnail_row_box text">
              <div className="masterProfile__name">{userProfile?.name}</div>
              <div className="masterProfile__email">{userProfile?.email}</div>
            </div>
          </div>
          <div className="masterProfile__info_wrapper">
            <div className="masterProfile__info_row">
              <div className="masterProfile__info_label">
                <T>GLOBAL_COUNTRY</T>
              </div>

              <div className="masterProfile__info_contents">
                {countryList?.find((i: ICountry) => i.id === userProfile?.countryId)?.name}
              </div>
            </div>
            <div className="masterProfile__info_row">
              <div className="masterProfile__info_label">
                <T>GLOBAL_ADDRESS</T>
              </div>
              <div className="masterProfile__info_contents">
                {userProfile?.countryId === 116 ? (
                  <>
                    {!!userProfile?.postcode && `${userProfile?.postcode}, `}
                    {!!userProfile?.district && `${userProfile?.district} `}
                    {!!userProfile?.city && `${userProfile?.city} `}
                    {!!userProfile?.address && `${userProfile?.address} `}
                  </>
                ) : (
                  <>
                    {!!userProfile?.address && `${userProfile?.address}, `}
                    {!!userProfile?.city && `${userProfile?.city}, `}
                    {!!userProfile?.district && `${userProfile?.district}, `}
                    {!!userProfile?.postcode && `${userProfile?.postcode} `}
                  </>
                )}
              </div>
            </div>
            <div className="masterProfile__info_row">
              <div className="masterProfile__info_label">
                <T>GLOBAL_PHONE</T>
              </div>
              <div className="masterProfile__info_contents">
                {!!userProfile?.phone ? (
                  <>
                    {'+' + userProfile.phonecode + ' ) '}
                    {userProfile.phone}
                  </>
                ) : (
                  '-'
                )}
              </div>
            </div>

            <div className="masterProfile__info_row">
              <div className="masterProfile__info_label">
                <T>MAIN_CURRENCY</T>
              </div>
              <div className="masterProfile__info_contents">
                {currencyList?.length > 0 && (
                  <MuiWrapper className="masterProfile__info_currency sm">
                    <FormControl fullWidth variant="outlined">
                      <StyledGlobalCurrencySelect />
                      <Select
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },

                          getContentAnchorEl: null,
                        }}
                        name="currency"
                        value={currency.value}
                        onChange={handleChangeCurrency}
                      >
                        {currencyList?.map((item: any, index: number) => {
                          return (
                            <MenuItem
                              value={item.code}
                              key={index}
                              className={cx('masterProfile__currency_select_item_container', {
                                selected: currency.value === item.code ? true : false,
                              })}
                            >
                              <div
                                className={cx('masterProfile__currency_select_item_box', {
                                  selected: currency.value === item.code ? true : false,
                                })}
                              >
                                <div>
                                  {item.feature} {item.name}
                                </div>
                                {user?.currencyCode === item.code && (
                                  <div
                                    className={cx('masterProfile__currency_select_main', {
                                      selected: currency.value === item.code ? true : false,
                                    })}
                                  >
                                    <T>GLOBAL_MAIN</T>
                                  </div>
                                )}
                              </div>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </MuiWrapper>
                )}
              </div>
            </div>

            <div className="masterProfile__info_row">
              <div className="masterProfile__info_label">
                <T>GLOBAL_PUBLIC</T>
                <Tooltip
                  className="masterProfile__Switch_tooltip"
                  title={tooltipText}
                  placement="top"
                  disableHoverListener={false}
                  disableFocusListener={true}
                  disableTouchListener={true}
                >
                  <div className="circle_exclamation_mark">
                    <span>!</span>
                  </div>
                </Tooltip>
              </div>
              <div className="masterProfile__info_contents">
                <div className="masterProfile__Switch_box">
                  <MuiWrapper>
                    <Switch
                      color="primary"
                      value={visibility.value}
                      checked={visibility.value}
                      className="masterProfile__Switch"
                      onChange={() => {
                        handleChangeVisibilty();
                      }}
                    />
                  </MuiWrapper>
                </div>
              </div>
            </div>
          </div>
        </div>

        {!!user.mesSerialNumber && (
          <div className="userSerial__box">
            <div className="userSerial__label">
              <T>MES_SERIAL_NO</T>
            </div>
            <div className="userSerial__contents">{user.mesSerialNumber}</div>
          </div>
        )}
      </div>

      <ProfileAuthConnectContainer />
    </MasterProfile>
  );
}

const StyledGlobalCurrencySelect = createGlobalStyle<{}>`

.masterProfile__currency_select_item_container {
  &.selected {
    background-color: #00A6E2 !important;
  }
  .masterProfile__currency_select_item_box {
  
    width: 100%;
    padding-right: 20px;
    display: flex;
    justify-content: space-between;
    font-weight: 400;
    color: #303030;
    &.selected {
      color: #ffffff;
    }
    .masterProfile__currency_select_main {
      width: 70px;
      height: 25px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
  
      border: 1px solid #69c9ee;
      border-radius: 13px;
      color: #69c9ee;
      font-weight: 400;
      &.selected {
        color: #ffffff;
        border: 1px solid #ffffff;
      }
  
    }
  }
}
`;

const MasterProfile = styled.section<{}>`
  .masterProfile__wrapper {
    background-color: #fff;
    position: relative;
    box-shadow: 0px 0px 10px rgb(0 0 0 / 16%);
    padding: 30px 0;
    margin-bottom: 20px;
    border-radius: 15px;
    width: 100%;
    .masterProfile__container {
      background-color: #ffffff;
      height: inherit;
      display: flex;

      &.modify {
        position: relative;
        justify-content: center;
      }
      .masterProfile__thumnail_container {
        width: 330px;
        border-right: 1px solid #e1e1e1;

        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        row-gap: 40px;
        .masterProfile__thumnail_row_box {
          &.text {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: center;
            row-gap: 10px;
            .masterProfile__name {
              font-size: 20px;
              font-weight: 500;
              line-height: 26px;
              color: #222222;
            }
            .masterProfile__email {
              font-size: 14px;
              font-weight: 200;
              line-height: 19px;
              color: #303030;
            }
          }
        }
        .masterProfile__image_box {
          display: block;
          position: relative;
          width: 130px;
          height: 130px;
          border-radius: 50%;
          overflow: hidden;
          box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.16);
          &.cursorPointer {
            &:hover {
              cursor: pointer;
            }
          }
          .masterProfile__image {
            width: 130px;
            height: 130px;
          }
          .masterProfile__profile_edit {
            width: 130px;
            height: 40px;
            position: absolute;
            bottom: 0;
            left: 0;
            opacity: 0.4;
            background-color: #000000;
            border-radius: 0 0 40px 40px / 0 0 40px 40px;
          }
          .masterProfile__profile_edit_text {
            font-size: 13px;
            font-weight: 400;
            position: absolute;
            color: #ffffff;
            background-color: transparent;
            margin-bottom: 15px;
            left: 50%;
            bottom: 0;
            transform: translate(-50%, -0);
          }
        }
      }

      .masterProfile__info_wrapper {
        padding-left: 30px;
        width: calc(100% - 330px);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        row-gap: 10px;
        .masterProfile__info_row {
          display: flex;
          align-items: center;
          .masterProfile__info_label {
            width: 150px;
            height: 40px;
            display: flex;
            align-items: center;
            font-size: 15px;
            font-weight: 500;
            color: #222;
          }
          .masterProfile__info_contents {
            width: calc(100% - 150px);
            height: 40px;
            display: flex;
            align-items: center;
            font-size: 14px;
            font-weight: 300;
            color: #444;
          }
          .masterProfile__info_currency {
            width: 200px;
          }
          .masterProfile__setting_software {
            width: 124px;
            height: 30px;
          }
        }
      }
      .circle_exclamation_mark {
        font-size: 15px;
        font-weight: 700;
        width: 20px;
        height: 20px;
        border-radius: 10px;
        border: 1px solid #b5b7c1;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #b5b7c1;
        margin-left: 10px;
        text-align: center;
        span {
          margin-left: -1px;
          margin-bottom: -2px;
        }

        &:hover {
          cursor: help;
        }
      }
      .masterProfile__Switch_box {
        display: flex;
        align-items: center;
        .masterProfile__Switch {
          padding: 7px;
          left: -7px;
          .MuiSwitch-track {
            border-radius: 12px;
          }
          .MuiSwitch-thumb {
            background-color: #ffffff;
          }
        }
      }
    }
  }

  .masterProfile__currency_select_item_box {
    width: 100%;
    padding-right: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 400;
    &.selected {
      color: #303030;
    }
    .masterProfile__currency_select_main {
      font-weight: 400;
      width: 70px;
      height: 25px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      border-radius: 13px;
      color: #69c9ee;
      &.selected {
        border: 1px solid #69c9ee;
      }
    }
  }

  .openFolder_button {
    padding: 5px;
  }

  .userSerial__box {
    position: absolute;
    right: 50px;
    bottom: 50px;
    display: flex;
    width: 200px;
    justify-content: space-between;
    .userSerial__label {
      font-size: 14px;
      font-weight: 500;
    }
    .userSerial__contents {
      font-size: 14px;
      font-weight: 300;
      color: #444;
    }
  }
`;
export default React.memo(MasterProfileContainer);
